import { useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Loader2, Send, CheckCircle2, ExternalLink, Eye, MessageSquare, Phone, AlertCircle, ShieldAlert } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import {
  updateEmployee, sendTipJarInvite, previewTipJarInvite, sendEmployeeSms,
  GetAdminEmployeesOutputType, PreviewTipJarInviteOutputType,
} from 'zite-endpoints-sdk';
import TipJarEmailPreview from './TipJarEmailPreview';

type Employee = GetAdminEmployeesOutputType['employees'][0];
const ALL_SKILLS = ['Chef', 'CDP', 'Sous Chef', 'Kitchen', 'Pot Wash', 'FOH Restaurant', 'FOH Bar', 'Events', 'Management', 'Agency', 'Trainer', 'Supervisor'];
const DEFAULT_TIPJAR_EMAIL = 'emily@wearetipjar.com';

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d), 'dd MMM yyyy, h:mm a') : null; } catch { return null; } }

const QUICK_MESSAGES = [
  'Hi! Just a quick reminder about your shift today. See you soon 👋',
  "Hey! Don't forget to check the rota — there's been an update to your schedule.",
  'Hi! Please log in to the Staff Hub at your earliest convenience — there\'s a message waiting for you.',
  'Your training module is ready! Please complete it before your next shift.',
  'Reminder: Policy sign-off required. Please log in to Rattlebone Staff Hub to complete it.',
];

interface Props { employee: Employee | null; open: boolean; onClose: () => void; onRefresh: () => void; }

export default function EmployeeDrawer({ employee, open, onClose, onRefresh }: Props) {
  const { user } = useAuth();
  const viewerIsAdmin = user?.role === 'Admin' || user?.role === 'Owner';

  const [saving, setSaving] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [tipJarEmail, setTipJarEmail] = useState(DEFAULT_TIPJAR_EMAIL);
  const [inviteSentAt, setInviteSentAt] = useState<string | undefined>();
  const [inviteSentTo, setInviteSentTo] = useState<string | undefined>();
  const [previewData, setPreviewData] = useState<PreviewTipJarInviteOutputType | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // SMS state
  const [smsMessage, setSmsMessage] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  const [lastSmsSent, setLastSmsSent] = useState<{ sid: string; to: string; time: string } | null>(null);

  const [form, setForm] = useState({
    role: '', department: '', position: '', employmentType: '', wage: '', holidayBalance: '', holidayAllowance: '',
    pensionStatus: '', probationStatus: '', onboardingStatus: '', tipJarReference: '',
    plandayId: '', xeroId: '', skills: [] as string[], phoneNumber: '',
  });

  const handleOpen = () => {
    if (!employee) return;
    setForm({
      role: employee.role || '', department: employee.department || '', position: employee.position || '',
      employmentType: employee.employmentType || '', wage: viewerIsAdmin ? (employee.wage?.toString() || '') : '',
      holidayBalance: employee.holidayBalance?.toString() || '',
      holidayAllowance: employee.holidayAllowance?.toString() || '',
      pensionStatus: employee.pensionStatus || '', probationStatus: employee.probationStatus || '',
      onboardingStatus: employee.onboardingStatus || '',
      tipJarReference: viewerIsAdmin ? (employee.tipJarReference || employee.email || '') : '',
      plandayId: viewerIsAdmin ? (employee.plandayId || '') : '',
      xeroId: viewerIsAdmin ? (employee.xeroId || '') : '',
      skills: employee.skills || [],
      phoneNumber: (employee as any).phoneNumber || '',
    });
    setTipJarEmail(DEFAULT_TIPJAR_EMAIL);
    setInviteSentAt(viewerIsAdmin ? employee.tipJarInviteSentAt : undefined);
    setInviteSentTo(viewerIsAdmin ? employee.tipJarInviteSentTo : undefined);
    setSmsMessage('');
    setLastSmsSent(null);
  };

  const toggleSkill = (s: string) => setForm(f => ({ ...f, skills: f.skills.includes(s) ? f.skills.filter(x => x !== s) : [...f.skills, s] }));

  const save = async () => {
    if (!employee) return;
    setSaving(true);
    try {
      const payload: any = {
        id: employee.id,
        role: form.role || undefined, department: form.department || undefined, position: form.position || undefined,
        employmentType: form.employmentType || undefined,
        holidayBalance: form.holidayBalance ? parseInt(form.holidayBalance) : undefined,
        holidayAllowance: form.holidayAllowance ? parseInt(form.holidayAllowance) : undefined,
        pensionStatus: form.pensionStatus || undefined, probationStatus: form.probationStatus || undefined,
        onboardingStatus: form.onboardingStatus || undefined,
        skills: form.skills,
        phoneNumber: form.phoneNumber || undefined,
      };
      // Only Owner/Admin can set sensitive fields
      if (viewerIsAdmin) {
        payload.wage = form.wage ? parseFloat(form.wage) : undefined;
        payload.tipJarReference = form.tipJarReference || undefined;
        payload.plandayId = form.plandayId || undefined;
        payload.xeroId = form.xeroId || undefined;
      }
      await updateEmployee(payload);
      toast.success('Employee updated');
      onRefresh();
      onClose();
    } catch (e: any) {
      toast.error(e.message || 'Failed to update');
    } finally { setSaving(false); }
  };

  const handlePreview = async () => {
    if (!employee) return;
    setLoadingPreview(true);
    try {
      const data = await previewTipJarInvite({ employeeId: employee.id, sendToEmail: tipJarEmail });
      setPreviewData(data);
      setShowPreview(true);
    } catch (e: any) { toast.error(e.message || 'Failed to load preview'); }
    finally { setLoadingPreview(false); }
  };

  const sendInvite = async () => {
    if (!employee) return;
    if (!tipJarEmail || !tipJarEmail.includes('@')) { toast.error('Please enter a valid email address'); return; }
    setSendingInvite(true);
    try {
      const res = await sendTipJarInvite({ employeeId: employee.id, sendToEmail: tipJarEmail });
      setInviteSentAt(res.sentAt);
      setInviteSentTo(res.sentTo);
      toast.success(`TipJar invite sent to ${res.sentTo}`);
    } catch (e: any) { toast.error(e.message || 'Failed to send invite'); }
    finally { setSendingInvite(false); }
  };

  const handleSendSms = async () => {
    if (!employee || !smsMessage.trim()) return;
    setSendingSms(true);
    try {
      const result = await sendEmployeeSms({
        employeeId: employee.id,
        message: smsMessage.trim(),
        ...(form.phoneNumber ? { phoneOverride: form.phoneNumber } : {}),
      });
      setLastSmsSent({ sid: result.sid || '', to: result.to, time: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) });
      setSmsMessage('');
      toast.success(`SMS sent to ${result.to} ✓`);
    } catch (e: any) { toast.error(e.message || 'Failed to send SMS'); }
    finally { setSendingSms(false); }
  };

  const isTipJarEligible = form.employmentType === 'Full Time' || form.employmentType === 'Part Time';
  const hasPhone = !!form.phoneNumber;

  const F = ({ label, fkey, opts, type = 'text', disabled = false }: { label: string; fkey: keyof typeof form; opts?: { v: string; l: string }[]; type?: string; disabled?: boolean }) => (
    <div>
      <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">{label}</label>
      {opts ? (
        <Select value={(form[fkey] as string) || 'none'} onValueChange={v => setForm(f => ({ ...f, [fkey]: v === 'none' ? '' : v }))} disabled={disabled}>
          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="none">—</SelectItem>{opts.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
        </Select>
      ) : <Input type={type} value={form[fkey] as string} onChange={e => setForm(f => ({ ...f, [fkey]: e.target.value }))} className="h-9" disabled={disabled} />}
    </div>
  );

  return (
    <>
      <Sheet open={open} onOpenChange={v => { if (!v) onClose(); else handleOpen(); }}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" onOpenAutoFocus={handleOpen}>
          <SheetHeader className="mb-5">
            <SheetTitle>{employee?.firstName} {employee?.lastName}</SheetTitle>
            <p className="text-sm text-muted-foreground">{employee?.email} · {employee?.employeeNumber ? `#${employee.employeeNumber}` : 'No employee number'}</p>
          </SheetHeader>

          {!viewerIsAdmin && (
            <div className="flex items-start gap-2 bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-xs text-muted-foreground mb-5">
              <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
              <span>Manager view — sensitive fields (wage, bank details, NI, payroll IDs) are hidden. Only Owner/Admin can view or edit these fields.</span>
            </div>
          )}

          <div className="space-y-5">
            {/* Role & Status */}
            <div className="grid grid-cols-2 gap-3">
              <F label="Role" fkey="role" opts={[{ v: 'Trainee', l: 'Trainee' }, { v: 'Staff', l: 'Staff' }, { v: 'Supervisor', l: 'Supervisor' }, { v: 'Manager', l: 'Manager' }, { v: 'Admin', l: 'Admin' }, { v: 'Owner', l: 'Owner' }]} />
              <F label="Onboarding Status" fkey="onboardingStatus" opts={[{ v: 'Not Started', l: 'Not Started' }, { v: 'In Progress', l: 'In Progress' }, { v: 'Submitted', l: 'Submitted' }, { v: 'Approved', l: 'Approved' }]} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <F label="Department" fkey="department" opts={[{ v: 'Management', l: 'Management' }, { v: 'Front of House', l: 'Front of House' }, { v: 'Back of House', l: 'Back of House' }, { v: 'Kitchen', l: 'Kitchen' }]} />
              <F label="Position / Job Title" fkey="position" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <F label="Employment Type" fkey="employmentType" opts={[{ v: 'Full Time', l: 'Full Time' }, { v: 'Part Time', l: 'Part Time' }, { v: 'Temporary', l: 'Temporary' }, { v: 'Casual', l: 'Casual' }]} />
              {viewerIsAdmin ? (
                <F label="Hourly Wage (£)" fkey="wage" type="number" />
              ) : (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Hourly Wage (£)</label>
                  <div className="h-9 flex items-center px-3 border border-border rounded-md bg-muted/30 text-muted-foreground text-sm">🔒 Restricted</div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <F label="Holiday Balance (days)" fkey="holidayBalance" type="number" />
              <F label="Holiday Allowance (days)" fkey="holidayAllowance" type="number" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <F label="Pension Status" fkey="pensionStatus" opts={[{ v: 'Enrolled', l: 'Enrolled' }, { v: 'Opted Out', l: 'Opted Out' }, { v: 'Not Eligible', l: 'Not Eligible' }]} />
              <F label="Probation Status" fkey="probationStatus" opts={[{ v: 'Active', l: 'Active' }, { v: 'Completed', l: 'Completed' }, { v: 'Extended', l: 'Extended' }]} />
            </div>

            {/* Phone Number */}
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1.5 block">
                <Phone className="w-3 h-3" /> Mobile Number
              </label>
              <Input
                value={form.phoneNumber}
                onChange={e => setForm(f => ({ ...f, phoneNumber: e.target.value }))}
                className="h-9"
                placeholder="e.g. 07700 900000"
                type="tel"
              />
              <p className="text-xs text-muted-foreground mt-1">Used for SMS notifications. UK numbers auto-formatted (07xxx → +447xxx).</p>
            </div>

            {/* Skills */}
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Skills & Capabilities</label>
              <div className="flex flex-wrap gap-1.5">
                {ALL_SKILLS.map(s => (
                  <button key={s} onClick={() => toggleSkill(s)} className={`text-xs px-2.5 py-1 border rounded-sm transition-colors ${form.skills.includes(s) ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}>{s}</button>
                ))}
              </div>
            </div>

            <Separator />

            {/* ── SMS Panel ── */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5" /> Send SMS via Twilio
                </p>
                {hasPhone && (
                  <span className="text-xs text-emerald-500 font-medium flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {form.phoneNumber}
                  </span>
                )}
              </div>

              {!hasPhone && (
                <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2.5 text-xs text-amber-600 dark:text-amber-400">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <strong>No phone number saved.</strong> Enter a mobile number above, then save before sending an SMS.
                  </div>
                </div>
              )}

              {/* Quick message buttons */}
              <div>
                <p className="text-xs text-muted-foreground mb-1.5">Quick messages:</p>
                <div className="flex flex-wrap gap-1.5">
                  {QUICK_MESSAGES.map((m, i) => (
                    <button key={i} onClick={() => setSmsMessage(m)} className="text-xs px-2 py-1 border border-border rounded hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors max-w-xs truncate text-left" title={m}>
                      {m.length > 40 ? m.slice(0, 40) + '…' : m}
                    </button>
                  ))}
                </div>
              </div>

              <Textarea value={smsMessage} onChange={e => setSmsMessage(e.target.value)} placeholder="Type your SMS message here…" rows={3} className="resize-none text-sm" maxLength={1600} />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{smsMessage.length}/1600 chars</span>
                {smsMessage.length > 160 && <span className="text-amber-500">Long message — may send as multiple SMS parts</span>}
              </div>

              <Button onClick={handleSendSms} disabled={sendingSms || !smsMessage.trim()} className="w-full bg-primary text-primary-foreground gap-2">
                {sendingSms ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending SMS…</> : <><MessageSquare className="w-4 h-4" /> Send SMS to {employee?.firstName || 'Employee'}</>}
              </Button>

              {lastSmsSent && (
                <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2.5 text-xs text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  <span>SMS delivered to <strong>{lastSmsSent.to}</strong> at {lastSmsSent.time}</span>
                </div>
              )}
            </div>

            {/* Payroll System IDs — Owner/Admin only */}
            {viewerIsAdmin && (
              <>
                <Separator />
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Payroll & Scheduling IDs</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Planday Employee ID</label>
                      <Input value={form.plandayId} onChange={e => setForm(f => ({ ...f, plandayId: e.target.value }))} className="h-9" placeholder="Planday reference number" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Xero Payroll ID</label>
                      <Input value={form.xeroId} onChange={e => setForm(f => ({ ...f, xeroId: e.target.value }))} className="h-9" placeholder="Xero employee / payroll ID" />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* TipJar Section — Owner/Admin only */}
            {viewerIsAdmin && (
              <>
                <Separator />
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">TipJar</p>

                  <div className="bg-amber-500/10 border border-amber-500/25 rounded-lg px-4 py-3 text-xs text-amber-600 dark:text-amber-400 mb-3 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                      <strong>Email delivery suppressed.</strong> The global email kill switch is ON. TipJar invite emails will not be delivered until the kill switch is turned off. You can still preview the email and save the TipJar reference.
                    </div>
                  </div>

                  {!isTipJarEligible && (
                    <div className="bg-muted/40 border border-border rounded-lg px-4 py-3 text-xs text-muted-foreground mb-3">
                      TipJar enrolment applies to <strong>Full Time</strong> and <strong>Part Time</strong> staff. This employee is listed as <strong>{form.employmentType || 'No type set'}</strong>.
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Employee's TipJar Username</label>
                    <Input value={form.tipJarReference} onChange={e => setForm(f => ({ ...f, tipJarReference: e.target.value }))} className="h-9" placeholder="TipJar username (once they've joined)" />
                  </div>

                  {inviteSentAt && (
                    <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-2.5 text-xs text-green-500 mb-3">
                      <CheckCircle2 className="w-4 h-4 shrink-0" />
                      <span>Invite sent to <strong>{inviteSentTo}</strong> on {fmtDate(inviteSentAt)}</span>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-xs text-muted-foreground uppercase tracking-wider block">
                      {inviteSentAt ? 'Resend Invite To' : 'Send Invite To'}
                    </label>
                    <Input value={tipJarEmail} onChange={e => setTipJarEmail(e.target.value)} className="h-9" placeholder="TipJar contact email" />
                    <div className="flex gap-2">
                      <Button onClick={handlePreview} disabled={loadingPreview || !employee} variant="outline" size="sm" className="h-9 flex-1">
                        {loadingPreview ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Loading...</> : <><Eye className="w-3.5 h-3.5 mr-1.5" /> Preview Email</>}
                      </Button>
                      <Button onClick={sendInvite} disabled={sendingInvite || !isTipJarEligible} size="sm" className="h-9 flex-1 bg-primary text-primary-foreground">
                        {sendingInvite ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Sending...</> : <><Send className="w-3.5 h-3.5 mr-1.5" /> {inviteSentAt ? 'Resend' : 'Send Invite'}</>}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Sends employee details to TipJar.{' '}
                      <a href="https://wearetipjar.com" target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-0.5 hover:underline">
                        wearetipjar.com <ExternalLink className="w-3 h-3" />
                      </a>
                    </p>
                  </div>
                </div>
              </>
            )}

            <Separator />

            <Button onClick={save} disabled={saving} className="w-full bg-primary text-primary-foreground">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Save Changes'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <TipJarEmailPreview open={showPreview} onClose={() => setShowPreview(false)} data={previewData} />
    </>
  );
}
